import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CustomBreadcrumbs from '../../components/common/CustomBreadcrumbs';
import PrimaryButton from '../../components/common/button/PrimaryButton';
import SecondaryButton from '../../components/common/button/SecondaryButton';
import { isLoggedIn } from '../../components/authProvider/AuthContext';
import { PastTransactionsApi } from '../../redux-state/pastTransactions/api';

// Tab value doubles as the backend's product key and the ?tab= query param.
const PRODUCTS = [
  { value: 'fees', label: 'Fees', file: 'Summary PDF' },
  { value: 'docs', label: 'Docs', file: 'Documents (.zip)' },
  { value: 'lien_title', label: 'Lien & Title Check', file: 'Yassi report PDF' },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// Date-time is archived as "YYYY-MM-DD HH:MM:SS" in Eastern time.
const formatDateTime = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(value || '');
  if (!match) return value || '';
  const [, year, month, day, hour, minute] = match;
  const h = Number(hour);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${month}/${day}/${year} ${hour12}:${minute} ${suffix} ET`;
};

export default function PastTransactions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const product = PRODUCTS.some((p) => p.value === requestedTab) ? requestedTab : 'fees';

  // Cached per product so flipping between tabs doesn't rescan the table.
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [downloading, setDownloading] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login');
    }
  });

  const load = useCallback(async (key) => {
    setLoading(true);
    const result = await PastTransactionsApi.list(key);
    if (result.status === 403) {
      navigate('/login');
      return;
    }
    setResults((prev) => ({ ...prev, [key]: result }));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    setPage(0);
    if (!results[product]) {
      load(product);
    }
  }, [product, results, load]);

  const handleTabChange = (event, newValue) => {
    setSearchParams({ tab: newValue });
  };

  const rowKey = (transactionId) => `${product}:${transactionId}`;

  const handleDownload = async (transactionId) => {
    const key = rowKey(transactionId);
    setDownloading((prev) => ({ ...prev, [key]: true }));
    setRowErrors((prev) => ({ ...prev, [key]: undefined }));

    const result = await PastTransactionsApi.download(product, transactionId);

    setDownloading((prev) => ({ ...prev, [key]: false }));
    if (result.error) {
      setRowErrors((prev) => ({ ...prev, [key]: result.message }));
    }
  };

  const current = results[product];
  const transactions = current?.transactions || [];
  const visibleRows = transactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const activeProduct = PRODUCTS.find((p) => p.value === product);

  return (
    <div className="p-4 lg:p-8 min-h-[700px]">
      {/* Back and Refresh share one row, side by side with a gap, rather than
          being stacked in two rows where they touched and didn't line up. */}
      <div className="flex flex-row justify-between items-start gap-4 mb-4">
        <CustomBreadcrumbs title="Past Transactions" subtitle={activeProduct.label} />
        <div className="flex flex-row items-center gap-3 shrink-0">
          <SecondaryButton
            text="Refresh"
            onClick={() => load(product)}
            disabled={loading}
          />
          <SecondaryButton text="Back" onClick={() => navigate('/dashboard')} />
        </div>
      </div>

      <div className="text-3xl font-bold text-gray-900 mb-2">Past Transactions</div>

      <Tabs
        value={product}
        classes={{ indicator: 'tabsIndicator' }}
        onChange={handleTabChange}
        aria-label="past transaction product tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        {PRODUCTS.map((p) => (
          <Tab
            key={p.value}
            value={p.value}
            label={p.label}
            className="text-10 md:text-14 tabs"
            id={`past-tab-${p.value}`}
            aria-controls={`past-tabpanel-${p.value}`}
          />
        ))}
      </Tabs>
      <hr className="divider" />

      <div
        role="tabpanel"
        id={`past-tabpanel-${product}`}
        aria-labelledby={`past-tab-${product}`}
        className="pt-4"
      >
        {product === 'lien_title' && (
          <div className="text-sm text-gray-600 mb-3">
            Lien &amp; title reports are re-pulled from Yassi each time you download, so they can take a few seconds.
          </div>
        )}

        {loading && !current ? (
          <div className="flex justify-center py-16">
            <CircularProgress sx={{ color: '#235D38' }} />
          </div>
        ) : current?.error ? (
          <Alert severity="error">{current.message}</Alert>
        ) : transactions.length === 0 ? (
          <div className="text-center text-gray-600 py-16">
            You have no past {activeProduct.label} transactions.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200">
            <TableContainer>
              <Table size="small" aria-label={`past ${activeProduct.label} transactions`}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Transaction ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date / Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{activeProduct.file}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => {
                    const key = rowKey(row.transaction_id);
                    const isDownloading = Boolean(downloading[key]);
                    const error = rowErrors[key];
                    return (
                      <TableRow key={row.transaction_id} hover>
                        <TableCell sx={{ wordBreak: 'break-all' }}>
                          <div>{row.transaction_id}</div>
                          {row.type && <div className="text-xs text-gray-500">{row.type}</div>}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.date_time)}</TableCell>
                        <TableCell align="right">
                          {row.available ? (
                            <div className="flex flex-col items-end gap-1">
                              <PrimaryButton
                                text={
                                  isDownloading
                                    ? <><CircularProgress size={16} sx={{ color: '#fff', mr: '6px' }} /> Downloading</>
                                    : <><DownloadIcon sx={{ mr: '6px' }} /> Download</>
                                }
                                onClick={() => handleDownload(row.transaction_id)}
                                disabled={isDownloading}
                              />
                              {error && <div className="text-xs text-red-600 max-w-xs text-right">{error}</div>}
                            </div>
                          ) : (
                            <Tooltip title="This transaction was run before reports could be re-pulled from Yassi.">
                              <span className="text-sm text-gray-500">Not available</span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={transactions.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </div>
        )}
      </div>
    </div>
  );
}
